import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import { useToast } from '@/shared/hooks/use-toast';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { FacetsService } from '../services/facetsService';
import { resolveApiErrorMessage, apiErrorTitle } from '@/shared/utils/apiErrorMessage';
import type { Facet, CreateFacetPayload, UpdateFacetPayload } from '../types';
import { rotuloDaDimensao, rotuloDoValor } from '../utils/labels';

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*$/;

interface EditingState {
  id: string | null;
  name: string;
  value: string;
  parent_facet_id: string;
  is_active: boolean;
  /**
   * O rótulo de tela por idioma (AB#85634).
   *
   * Separado de `name`/`value` de propósito: aqueles são identificadores, e o
   * banco os exige minúsculos e sem acento. É aqui que se escreve
   * "Departamento" sem criar uma segunda faceta para a mesma coisa.
   */
  labelName: Record<string, string>;
  labelValue: Record<string, string>;
}

/** Os idiomas da aplicação — os mesmos do resto do produto. */
const IDIOMAS = [
  { id: 'pt', rotulo: 'Português' },
  { id: 'en', rotulo: 'English' },
];

const emptyEditing: EditingState = {
  id: null,
  name: '',
  value: '',
  parent_facet_id: '',
  is_active: true,
  labelName: {},
  labelValue: {},
};

/** Descarta idioma sem texto: mapa vazio no banco diria que alguém traduziu. */
const semVazios = (mapa: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(mapa).filter(([, texto]) => texto.trim().length > 0));

export const FacetsSettings = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const orgId = user?.organizationId ?? null;

  const [editing, setEditing] = useState<EditingState>(emptyEditing);
  const [idioma, setIdioma] = useState(IDIOMAS[0]!.id);
  const [showInactive, setShowInactive] = useState(false);

  const { data: facets = [], isLoading } = useQuery({
    queryKey: ['facets', orgId, showInactive],
    queryFn: () => (orgId ? FacetsService.list(orgId, showInactive) : Promise.resolve<Facet[]>([])),
    enabled: !!orgId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['facets', orgId] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateFacetPayload) => FacetsService.create(orgId!, payload),
    onSuccess: () => {
      toast({ title: t('facets.success.created') });
      setEditing(emptyEditing);
      invalidate();
    },
    onError: (e: Error) => toast({ title: apiErrorTitle(t), description: resolveApiErrorMessage(e, t), variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; payload: UpdateFacetPayload }) => FacetsService.update(orgId!, vars.id, vars.payload),
    onSuccess: () => {
      toast({ title: t('facets.success.updated') });
      setEditing(emptyEditing);
      invalidate();
    },
    onError: (e: Error) => toast({ title: apiErrorTitle(t), description: resolveApiErrorMessage(e, t), variant: 'destructive' }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => FacetsService.remove(orgId!, id),
    onSuccess: () => {
      toast({ title: t('facets.success.deleted') });
      invalidate();
    },
    onError: (e: Error) => toast({ title: apiErrorTitle(t), description: resolveApiErrorMessage(e, t), variant: 'destructive' }),
  });

  const seedMutation = useMutation({
    mutationFn: () => FacetsService.seedDefaults(orgId!),
    onSuccess: () => {
      toast({ title: t('facets.success.seeded') });
      invalidate();
    },
    onError: (e: Error) => toast({ title: apiErrorTitle(t), description: resolveApiErrorMessage(e, t), variant: 'destructive' }),
  });

  const validateForm = (): string | null => {
    if (!editing.name) return t('facets.errors.nameRequired');
    if (!editing.value) return t('facets.errors.valueRequired');
    if (!SLUG_REGEX.test(editing.name) || !SLUG_REGEX.test(editing.value)) return t('facets.errors.invalidFormat');
    return null;
  };

  const handleSave = () => {
    const err = validateForm();
    if (err) {
      toast({ title: err, variant: 'destructive' });
      return;
    }
    const rotulos = {
      ...(Object.keys(semVazios(editing.labelName)).length > 0
        ? { name: semVazios(editing.labelName) }
        : {}),
      ...(Object.keys(semVazios(editing.labelValue)).length > 0
        ? { value: semVazios(editing.labelValue) }
        : {}),
    };

    const payload: CreateFacetPayload = {
      name: editing.name,
      value: editing.value,
      parent_facet_id: editing.parent_facet_id || null,
      // Nulo apaga o rótulo; é como se desfaz um texto escrito errado.
      labels: Object.keys(rotulos).length > 0 ? rotulos : null,
    };
    if (editing.id) {
      updateMutation.mutate({
        id: editing.id,
        payload: { ...payload, is_active: editing.is_active },
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const startEdit = (f: Facet) => {
    setEditing({
      id: f.id,
      name: f.name,
      value: f.value,
      parent_facet_id: f.parent_facet_id ?? '',
      is_active: f.is_active,
      labelName: f.labels?.name ?? {},
      labelValue: f.labels?.value ?? {},
    });
  };

  // O rótulo cadastrado vem primeiro; sem ele, a tradução embutida; sem ela, o
  // identificador (AB#85634, task #86093).
  const labelFor = (slug: string, facet?: Facet): string =>
    rotuloDoValor(slug, i18n.language, t, facet);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('facets.title')}</CardTitle>
        <CardDescription>{t('facets.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Form */}
        <div className="rounded-md border p-4 space-y-3">
          <h3 className="text-sm font-medium">{editing.id ? t('facets.edit') : t('facets.add')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="facet-name">{t('facets.fields.name')}</Label>
              <Input
                id="facet-name"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value.trim().toLowerCase() })}
                placeholder="type"
              />
              <p className="text-xs text-muted-foreground mt-1">{t('facets.fields.nameHelp')}</p>
            </div>
            <div>
              <Label htmlFor="facet-value">{t('facets.fields.value')}</Label>
              <Input
                id="facet-value"
                value={editing.value}
                onChange={(e) => setEditing({ ...editing, value: e.target.value.trim().toLowerCase() })}
                placeholder="meeting"
              />
              <p className="text-xs text-muted-foreground mt-1">{t('facets.fields.valueHelp')}</p>
            </div>
          </div>

          {/* O RÓTULO, que é o que a tela mostra. Os dois campos acima são
              identificadores e continuam em inglês e minúsculos — traduzi-los
              criaria duas facetas para a mesma coisa, e o filtro casa por
              igualdade exata. */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="inline-flex h-8 items-center rounded-md border bg-muted/40 p-0.5">
              {IDIOMAS.map((opcao) => (
                <button
                  key={opcao.id}
                  type="button"
                  onClick={() => setIdioma(opcao.id)}
                  className={
                    idioma === opcao.id
                      ? 'rounded-[5px] bg-background px-3 py-1 text-xs font-medium shadow-sm'
                      : 'rounded-[5px] px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground'
                  }
                >
                  {opcao.rotulo}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="facet-label-name">{t('facets.fields.labelName')}</Label>
                <Input
                  id="facet-label-name"
                  value={editing.labelName[idioma] ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      labelName: { ...editing.labelName, [idioma]: e.target.value },
                    })
                  }
                  placeholder={editing.name}
                />
              </div>
              <div>
                <Label htmlFor="facet-label-value">{t('facets.fields.labelValue')}</Label>
                <Input
                  id="facet-label-value"
                  value={editing.labelValue[idioma] ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      labelValue: { ...editing.labelValue, [idioma]: e.target.value },
                    })
                  }
                  placeholder={editing.value}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="facet-parent">{t('facets.fields.parent')}</Label>
            <Select
              value={editing.parent_facet_id || '__none__'}
              onValueChange={(v) => setEditing({ ...editing, parent_facet_id: v === '__none__' ? '' : v })}
            >
              <SelectTrigger id="facet-parent">
                <SelectValue placeholder={t('facets.fields.none')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('facets.fields.none')}</SelectItem>
                {facets.filter((f) => f.id !== editing.id).map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}: {labelFor(f.value, f)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{t('facets.fields.parentHelp')}</p>
          </div>

          {editing.id && (
            <div className="flex items-center gap-2">
              <Switch
                id="facet-active"
                checked={editing.is_active}
                onCheckedChange={(c) => setEditing({ ...editing, is_active: c })}
              />
              <Label htmlFor="facet-active">{t('facets.fields.isActive')}</Label>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Plus className="h-4 w-4 mr-2" /> {editing.id ? t('facets.edit') : t('facets.add')}
            </Button>
            {editing.id && (
              <Button variant="outline" onClick={() => setEditing(emptyEditing)}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
            <Label htmlFor="show-inactive">{t('facets.disable')} / {t('facets.enable')}</Label>
          </div>
          <Button variant="outline" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
            {seedMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('facets.seedDefaults')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : facets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t('facets.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('facets.fields.name')}</TableHead>
                <TableHead>{t('facets.fields.value')}</TableHead>
                <TableHead>{t('facets.fields.parent')}</TableHead>
                <TableHead>{t('facets.fields.isActive')}</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {facets.map((f) => {
                const parent = facets.find((p) => p.id === f.parent_facet_id);
                return (
                  <TableRow key={f.id} className={!f.is_active ? 'opacity-50' : ''}>
                    <TableCell className="text-xs">
                      <span className="font-mono">{f.name}</span>
                      <span className="text-muted-foreground ml-2">
                        ({rotuloDaDimensao(f.name, i18n.language, t, f)})
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{f.value}</span>
                      <span className="text-muted-foreground ml-2">({labelFor(f.value, f)})</span>
                    </TableCell>
                    <TableCell>{parent ? `${parent.name}:${parent.value}` : t('facets.fields.none')}</TableCell>
                    <TableCell>{f.is_active ? '✓' : '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(f)} title={t('facets.edit')}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(t('facets.confirmDelete.description'))) {
                            removeMutation.mutate(f.id);
                          }
                        }}
                        title={t('facets.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

      </CardContent>
    </Card>
  );
};
